import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { Download, Search } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { DEMO_FLIGHTS, DEMO_LESSEES } from '../../lib/demoData';
import type { FlightDoc, UserDoc } from '../../lib/types';
import { PageHeader } from '../../components/PageHeader';
import { Spinner } from '../../components/Spinner';
import { DateRangeChips } from '../../components/DateRangeChips';
import { DATE_RANGES, inDateRange, type DateRange } from '../../lib/flightFilters';
import { FlightEditModal } from '../../components/FlightEditModal';
import { TRACKED_REG } from '../../lib/flightTrack';

// Why a flight wasn't billed. Without this a $0.00 row looks like a mistake.
const BILLING_NOTE: Record<string, string> = {
  owner: 'Owner flight — not billed',
  comped: 'Comped — not billed',
  checkout: 'Checkout flight — not billed',
  promo: 'Promo rate',
};

export function AdminFlights() {
  const { demoMode } = useAuth();
  const [flights, setFlights] = useState<FlightDoc[] | null>(null);
  const [range, setRange] = useState<DateRange>('this_month');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<FlightDoc | null>(null);
  // Pilot names came from lesseeByUid(), which only ever searched the DEMO data —
  // so in production every card rendered a blank name, search-by-pilot matched
  // nothing, and the CSV said "Pilot" for everyone. Read the real users.
  const [names, setNames] = useState<Record<string, string>>({});

  useEffect(() => {
    if (demoMode) {
      setFlights(DEMO_FLIGHTS.slice().sort((a, b) => (a.date < b.date ? 1 : -1)));
      setNames(
        Object.fromEntries(
          DEMO_LESSEES.map((u) => [
            u.uid,
            `${u.personal?.firstName ?? ''} ${u.personal?.lastName ?? ''}`.trim() || u.email,
          ])
        )
      );
      return;
    }
    if (!db) return;
    const unsub = onSnapshot(
      query(collection(db, 'flights'), orderBy('date', 'desc')),
      (snap) => setFlights(snap.docs.map((d) => ({ ...(d.data() as FlightDoc), flightId: d.id })))
    );
    const unsubUsers = onSnapshot(collection(db, 'users'), (snap) =>
      setNames(
        Object.fromEntries(
          snap.docs.map((d) => {
            const u = d.data() as UserDoc;
            return [
              d.id,
              `${u.personal?.firstName ?? ''} ${u.personal?.lastName ?? ''}`.trim() || u.email || 'Pilot',
            ];
          })
        )
      )
    );
    return () => {
      unsub();
      unsubUsers();
    };
  }, [demoMode]);

  const nameFor = (f: FlightDoc) => names[f.lesseeUid] ?? 'Pilot';

  const all = flights ?? [];
  const matches = (f: FlightDoc) =>
    inDateRange(f.date, range) &&
    (!search.trim() || nameFor(f).toLowerCase().includes(search.trim().toLowerCase()));
  const filtered = all.filter(matches);
  const counts = Object.fromEntries(
    DATE_RANGES.map((r) => [r, all.filter((f) => inDateRange(f.date, r)).length])
  ) as Record<DateRange, number>;
  const totalHours = filtered.reduce((s, f) => s + f.hoursFlown, 0);
  const totalRevenue = filtered.reduce((s, f) => s + f.totalCharge, 0);

  // Export reflects the current view (date range + search). Switch to "All" to export everything.
  function exportCsv() {
    if (!filtered.length) return;
    const rows = [
      ['Date', 'Pilot', 'Aircraft', 'From', 'To', 'Hobbs Out', 'Hobbs In', 'Hours', 'Pax', 'Charge'].join(','),
      ...filtered.map((f) =>
        [f.date, nameFor(f), f.aircraftId, f.departureAirport, f.destinations.join('-'), f.hobbsOut, f.hobbsIn, f.hoursFlown, f.passengerCount, f.totalCharge].join(',')
      ),
    ];
    const url = URL.createObjectURL(new Blob([rows.join('\n')], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `ajm-flights-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <PageHeader
        title="All flights"
        subtitle={`Every flight logged for ${TRACKED_REG}.`}
        actions={
          flights && flights.length > 0 ? (
            <button onClick={exportCsv} className="btn-secondary inline-flex items-center gap-1.5 text-sm">
              <Download size={14} /> Export CSV
            </button>
          ) : undefined
        }
      />
      {!flights ? (
        <Spinner label="Loading flights" />
      ) : flights.length === 0 ? (
        <div className="card p-8 text-center text-ink-muted">No flights logged yet.</div>
      ) : (
        <>
          <div className="mb-4 space-y-3">
            <DateRangeChips value={range} onChange={setRange} counts={counts} />
            <div className="relative">
              <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
              <input
                className="input pl-9"
                placeholder="Search by pilot name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="mb-4 grid grid-cols-3 gap-3">
            <Stat label="Flights" value={String(filtered.length)} />
            <Stat label="Hobbs hrs" value={totalHours.toFixed(1)} />
            <Stat label="Revenue" value={`$${totalRevenue.toFixed(2)}`} />
          </div>
          {filtered.length === 0 ? (
            <div className="card p-8 text-center text-ink-muted">
              No flights match. Try a wider range or clear the search.
            </div>
          ) : (
          <div className="space-y-3">
          {filtered.map((f) => {
            const note = f.billingMode && f.billingMode !== 'standard' ? BILLING_NOTE[f.billingMode] : null;
            const legs = (f.destinations ?? []).filter(Boolean);
            const route = legs.length
              ? [f.departureAirport, ...legs].join(' → ')
              : `${f.departureAirport} local`;
            const when = new Date(`${f.date}T12:00:00`).toLocaleDateString([], {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            });
            return (
              <div key={f.flightId} onClick={() => setEditing(f)} className="card cursor-pointer p-4 transition active:scale-[0.99] sm:p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    {/* Who flew is the whole point of this list and used to render blank. */}
                    <div className="font-display text-base font-semibold text-navy-900">{nameFor(f)}</div>
                    <div className="mt-0.5 text-sm text-ink-muted">
                      {when} &middot; {route}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-display text-base font-semibold text-navy-900">
                      ${f.totalCharge.toFixed(2)}
                    </div>
                    <div className="mt-0.5 text-sm text-ink-muted">{f.hoursFlown.toFixed(1)} hrs</div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 pt-3 text-sm">
                  <span className="text-ink">
                    <span className="text-ink-muted">Hobbs</span> {f.hobbsOut?.toFixed(1)} →{' '}
                    {f.hobbsIn?.toFixed(1)}
                  </span>
                  <span className="text-ink">
                    <span className="text-ink-muted">Tach</span> {f.tachOut?.toFixed(1)} →{' '}
                    {f.tachIn?.toFixed(1)}
                  </span>
                  {f.passengerCount > 0 && (
                    <span className="text-ink">
                      <span className="text-ink-muted">Pax</span> {f.passengerCount}
                    </span>
                  )}
                  {/* A $0.00 row reads as a bug unless it says why. */}
                  {note && <span className="badge-neutral">{note}</span>}
                  {f.paymentMethod === 'cash' && <span className="badge-warning">Cash</span>}
                </div>
              </div>
            );
          })}
          </div>
          )}
        </>
      )}
      {editing && <FlightEditModal flight={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-3 text-center sm:p-4">
      <div className="text-[11px] uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="mt-0.5 font-display text-lg font-semibold text-navy-900 sm:text-xl">{value}</div>
    </div>
  );
}
